/**
 * Browser-safe request context store.
 * On the server, AsyncLocalStorage registers its getter via registerGetStore().
 * On the browser, getRequestContext() always returns undefined (unused by apiClientBrowser).
 */
interface RequestContextStore {
  origin: string;
  hostname: string;
  languageCode: string | undefined;
  // Mutable holder: server-only components (ProtectedRoute) can request a redirect
  // during SSR render, which the server issues as a 302 after render — see setAuthRedirect.
  authRedirect?: { to?: string };
  // Mutable holder: set when a route's data doesn't exist; see setNotFound
  notFound?: { value?: boolean };
  // Mutable holder: raw translations the render used; see recordUsedTranslation
  usedTranslations?: Record<string, string>;
}

type GetStoreFn = () => RequestContextStore | undefined;

let getStoreFn: GetStoreFn = () => undefined;

export const registerGetStore = (fn: GetStoreFn): void => {
  getStoreFn = fn;
};

export const getRequestContext = (): RequestContextStore | undefined => getStoreFn();

/**
 * Server-only in practice: records the URL a protected route wants to redirect an
 * unauthenticated request to, so the SSR server can respond with a 302 instead of
 * painting the protected shell. No-op on the browser (no request context registered).
 */
export const setAuthRedirect = (to: string): void => {
  const store = getStoreFn();
  if (store?.authRedirect) {
    store.authRedirect.to = to;
  }
};

export const getAuthRedirect = (): string | undefined => getStoreFn()?.authRedirect?.to;

/**
 * Server-only in practice: marks the response as a real 404 (e.g. a bsdir path the resolver
 * doesn't know), so the SSR server doesn't answer 200 for a page that doesn't exist.
 * No-op on the browser (no request context registered).
 */
export const setNotFound = (): void => {
  const store = getStoreFn();
  if (store?.notFound) {
    store.notFound.value = true;
  }
};

export const getNotFound = (): boolean => Boolean(getStoreFn()?.notFound?.value);

/**
 * Server-only in practice: records a raw translation the SSR render used, so the page can ship
 * it and the browser hydrates with the same text. No-op on the browser.
 */
export const recordUsedTranslation = (key: string, value: string): void => {
  const store = getStoreFn();
  if (store?.usedTranslations) {
    store.usedTranslations[key] = value;
  }
};

export const getUsedTranslations = (): Record<string, string> => getStoreFn()?.usedTranslations ?? {};
